import { useId } from 'react';
import { BannerChevronIcon } from '../../../assets/svgImages';
import {
  DEFAULT_AUTOPLAY_INTERVAL,
  SWIPE_THRESHOLD_PX,
  DESKTOP_ARROW_BASE_CLASSNAME,
  MOBILE_ARROW_BUTTON_CLASSNAME,
} from '../../../constant/constant';
import { useCarouselAutoPlay } from '../../../hooks/useCarouselAutoPlay';
import { useCarouselNavigation } from '../../../hooks/useCarouselNavigation';
import { useHoverFocusPause } from '../../../hooks/useHoverFocusPause';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion';
import { useSwipeGesture } from '../../../hooks/useSwipeGesture';
import { BannerCarouselProps } from '../../../interface/interface';
import BannerSlidePanel from './BannerSlidePanel';

export default function BannerCarousel({
  slides,
  autoPlay = true,
  autoPlayInterval = DEFAULT_AUTOPLAY_INTERVAL,
  showArrows = true,
  showDots = true,
  className = '',
}: Readonly<BannerCarouselProps>) {
  const slideCount = slides.length;
  const carouselId = useId();
  const prefersReducedMotion = usePrefersReducedMotion();

  const { activeIndex, announcement, goToSlide, goNext, goPrev } =
    useCarouselNavigation(slideCount);

  const {
    isPaused,
    handleMouseEnter,
    handleMouseLeave,
    handleFocus,
    handleBlur,
  } = useHoverFocusPause();

  const isAutoPlayEnabled = autoPlay && !prefersReducedMotion && slideCount > 1;

  useCarouselAutoPlay({
    enabled: isAutoPlayEnabled && !isPaused,
    interval: autoPlayInterval,
    activeIndex,
    onAdvance: goNext,
  });

  const { onTouchStart: handleTouchStart, onTouchEnd: handleTouchEnd } =
    useSwipeGesture({
      onSwipeLeft: () => goNext(),
      onSwipeRight: () => goPrev(),
      threshold: SWIPE_THRESHOLD_PX,
      disabled: slideCount <= 1,
    });

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (slideCount <= 1) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goPrev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      goNext();
    }
  };

  const renderDots = () =>
    slides.map((slide, index) => (
      <button
        key={slide.id}
        type="button"
        aria-label={`Go to slide ${index + 1}`}
        aria-current={index === activeIndex ? 'true' : undefined}
        aria-controls={`${carouselId}-slide-${index}`}
        onClick={() => goToSlide(index)}
        className={`h-2.5 rounded-full transition-all duration-300
          focus-visible:outline focus-visible:outline-2
          focus-visible:outline-offset-2 focus-visible:outline-primary-blue-500 ${
            index === activeIndex
              ? 'w-6 bg-primary-blue-500'
              : 'w-2.5 bg-primary-blue-100 hover:bg-primary-blue-200'
          }`}
      />
    ));

  if (slideCount === 0) return null;

  return (
    <section
      aria-roledescription="carousel"
      aria-label="Promotional banners"
      className={`relative w-full ${className}`.trim()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </span>

      <div className="relative overflow-hidden">
        <div
          className="flex"
          style={{
            transform: `translateX(-${activeIndex * 100}%)`,
            transition: prefersReducedMotion
              ? 'none'
              : 'transform 700ms cubic-bezier(0.65, 0, 0.35, 1)',
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {slides.map((slide, index) => (
            <BannerSlidePanel
              key={slide.id}
              slide={slide}
              isActive={index === activeIndex}
              slideNumber={index + 1}
              slideCount={slideCount}
              panelId={`${carouselId}-slide-${index}`}
            />
          ))}
        </div>

        {showArrows && slideCount > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous slide"
              onClick={() => goPrev()}
              className={`${DESKTOP_ARROW_BASE_CLASSNAME} left-4 lg:left-6`}
            >
              <BannerChevronIcon className="h-5 w-5" />
            </button>

            <button
              type="button"
              aria-label="Next slide"
              onClick={() => goNext()}
              className={`${DESKTOP_ARROW_BASE_CLASSNAME} right-4 lg:right-6`}
            >
              <BannerChevronIcon className="h-5 w-5 rotate-180" />
            </button>
          </>
        )}

        {showDots && slideCount > 1 && (
          <div
            role="group"
            aria-label="Slide navigation"
            className="absolute bottom-4 left-1/2 z-20 hidden -translate-x-1/2 gap-2 md:flex"
          >
            {renderDots()}
          </div>
        )}
      </div>

      {(showArrows || showDots) && slideCount > 1 && (
        <div className="flex items-center justify-center gap-4 py-4 md:hidden">
          {showArrows && (
            <button
              type="button"
              aria-label="Previous slide"
              onClick={() => goPrev()}
              className={MOBILE_ARROW_BUTTON_CLASSNAME}
            >
              <BannerChevronIcon className="h-4 w-4" />
            </button>
          )}

          {showDots && (
            <div
              role="group"
              aria-label="Slide navigation"
              className="flex gap-2"
            >
              {renderDots()}
            </div>
          )}

          {showArrows && (
            <button
              type="button"
              aria-label="Next slide"
              onClick={() => goNext()}
              className={MOBILE_ARROW_BUTTON_CLASSNAME}
            >
              <BannerChevronIcon className="h-4 w-4 rotate-180" />
            </button>
          )}
        </div>
      )}
    </section>
  );
}
